import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaPlus, FaUserPlus } from 'react-icons/fa';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YTable } from '../../../ui/YTable';
import {
  createClubMemberInvite,
  fetchClubMemberInvites,
  inviteSentToast,
  memberInviteErrorMessage,
  searchAvailableClubUsers,
} from '../services/memberInviteService';

export function AddMemberAccordion({ clubId, existingMembers = [], onInvited, className = '' }) {
  const { show: showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const debounceRef = useRef(null);

  const existingIds = useMemo(
    () => new Set((existingMembers || []).map((m) => Number(m.id))),
    [existingMembers]
  );

  const loadPending = useCallback(async () => {
    if (!clubId) return;
    try {
      const rows = await fetchClubMemberInvites(clubId, { status: 'pending' });
      setPendingIds(new Set(rows.map((row) => Number(row.invitee_id)).filter((id) => Number.isFinite(id))));
    } catch (_) {
      setPendingIds(new Set());
    }
  }, [clubId]);

  useEffect(() => {
    if (!open) return;
    loadPending();
  }, [open, loadPending]);

  const runSearch = useCallback(
    async (q) => {
      if (!q || q.trim().length < 2) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const items = await searchAvailableClubUsers(clubId, q.trim());
        setResults(items || []);
      } catch (_) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [clubId]
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, query, runSearch]);

  const handleInvite = useCallback(
    async (userId) => {
      setInvitingId(userId);
      setError('');
      try {
        const data = await createClubMemberInvite(clubId, userId);
        setPendingIds((prev) => new Set([...prev, Number(userId)]));
        try {
          showToast?.(inviteSentToast(data?.status));
        } catch (_) {
          /* toast optional in tests */
        }
        if (typeof onInvited === 'function') onInvited(userId, data);
      } catch (err) {
        setError(memberInviteErrorMessage(err, 'Could not send that invite.'));
      } finally {
        setInvitingId(null);
      }
    },
    [clubId, onInvited, showToast]
  );

  return (
    <div className={className}>
      <div className="d-grid">
        <YButton
          variant="secondary"
          onClick={() => setOpen((o) => !o)}
          className="d-flex align-items-center justify-content-center"
        >
          <FaUserPlus className="me-2" />
          Invite from directory
        </YButton>
      </div>
      {open && (
        <div className="mt-2" role="region" aria-label="invite-from-directory">
          <p className="text-muted small mb-2">
            Search Yona users. They join the roster only after they accept.
          </p>
          <div className="mb-2">
            <YInput
              type="text"
              placeholder="Search users by name or username (min 2 chars)"
              aria-label="search-users"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {error ? (
            <p className="text-danger small" role="alert">
              {error}
            </p>
          ) : null}

          {loading ? (
            <div className="d-flex align-items-center gap-2 text-muted" aria-busy="true">
              <Spinner animation="border" size="sm" />
              Searching…
            </div>
          ) : (
            <YTable>
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ width: 160 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(results || []).map((u) => {
                  const alreadyMember = existingIds.has(Number(u.id));
                  const alreadyInvited = pendingIds.has(Number(u.id));
                  const busy = invitingId === u.id;
                  return (
                    <tr key={u.id}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold">{u.username}</span>
                          {(u.first_name || u.last_name) && (
                            <span className="text-muted small">
                              {[u.first_name, u.last_name].filter(Boolean).join(' ')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <YButton
                          variant="primary"
                          size="sm"
                          disabled={alreadyMember || alreadyInvited || busy}
                          onClick={() => handleInvite(u.id)}
                          className="d-inline-flex align-items-center"
                          data-testid={`invite-user-${u.id}`}
                        >
                          <FaPlus className="me-1" />
                          {alreadyMember ? 'Member' : alreadyInvited ? 'Invited' : busy ? 'Sending…' : 'Invite'}
                        </YButton>
                      </td>
                    </tr>
                  );
                })}
                {(!results || results.length === 0) && (
                  <tr>
                    <td colSpan={2} className="text-muted small">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </YTable>
          )}
        </div>
      )}
    </div>
  );
}

export default AddMemberAccordion;
